import React from 'react';
import { compose, omit, path, pick, prop, identity } from 'ramda';
import { withTranslations } from 'js/components/hocs/withTranslations';
import { useAppSelector, useDeviceSelector, useGameConfigurationSelector, useUserSelector } from 'js/store/hooks';
import { branch, mapProps, pure, renderNothing, shouldUpdate, withHandlers, withProps, withState } from 'recompose';
import { storageService } from 'js/services/storage.service';
import { usePortletConfig } from 'js/components/system/Portlet';
import { FavoriteIcon } from '@core/js/components/common/Game/FavoriteIcon/FavoriteIcon';
import withGameLaunch from 'js/components/common/Game/withGameLaunch';
import { withGameInfo } from 'js/components/common/Game/withGameInfo';
import GameImage from './GameImage';
import { Image } from 'js/components/common/Image';

import style from './Game.scss';
import { useGameImageUrls, useLiveGameConfig } from '@core/js/hooks/games';
import { useGameConfig } from '@theme/js/components/common/Game/hooks';

export const GameBare = ({
                             t,
                             code,
                             name,
                             size = 'extraSmall',
                             showTitle = false,
                             showFeatureIcons = true,
                             gameFeed,
                             showFavoriteIcon,
                             showRecentlyPlayedIcon,
                             className = '',
                             setGameRef,
                             setImageRef,
                             setGameImageRef,
                             onClick,
                             onMouseMove,
                             isLoggedIn,
                             isGameNew,
                             isGameHot,
                             isGamePopular,
                             isGameExclusive,
                             liveLaunchAlias,
                             includeLiveGames = false,
                             isMobileSpecialCategory,
                             isLoadedGameImage,
                             setLoadGameImage,
                             portletType,
                             isDesktop,
                             isExpandedCategoryPopup,
                             seoFriendlyGameName,
                             isPinned
                         }) => {
    const liveGameConfig = useLiveGameConfig(liveLaunchAlias);
    const isLiveGame = includeLiveGames && liveGameConfig?.launchAlias;
    const { dynamicImageUrl, limits, isScheduled, openingDateTime, dealerName, language, seatsInfo } = liveGameConfig || {};
    const isScheduledLiveGame = isLiveGame && isScheduled;
    const imageUrls = useGameImageUrls({ code, dynamicImageUrl, isLiveGame });
    const dynamicTags = useAppSelector(prop('dynamicTags'));
    const gameLinkTag = dynamicTags && dynamicTags.DTAG_GAMEINFO_POPUP_LINK;
    const gameLink = gameLinkTag || '/online-game';
    const {
        images = {}
    } = useGameConfig(code);

    return (
        <a href={`${gameLink}/${seoFriendlyGameName}`}
           onClick={onClick}
           className={`${style.container} ${style[size]} ${className} ${isLoadedGameImage ? style.loadedGame : style.loadingGame} ${isLiveGame ? style.liveGame : ''}`}
           data-game-code={code}
           ref={setGameRef}
           aria-label={name}
        >
            <div
                ref={setGameImageRef}
                onMouseMove={onMouseMove}
                className={style.imageContainer}
            >
                {portletType === 'responsivegames' || isExpandedCategoryPopup ?
                    isLiveGame ? <Image
                            alt={name}
                            src={imageUrls}
                            ref={setImageRef}
                            className={style.image}
                        /> :
                        <GameImage
                            className={style.image}
                            images={images}
                            isMobileSpecialCategory={isMobileSpecialCategory}
                            isLoggedIn={isLoggedIn}
                            setLoadGameImage={setLoadGameImage}
                            portletType={portletType}
                            isDesktop={isDesktop}
                            alt={name}
                            gameCode={code}
                        />
                 : (
                    <Image
                        src={!isLoggedIn && images.guest || images.base}
                        className={style.image}
                        alt={name}
                    />
                )}
                {isScheduledLiveGame && <div className={style.scheduledLiveGameBackground} />}
                {(showFeatureIcons || showFavoriteIcon) && <div className={style.background} />}
                {showRecentlyPlayedIcon && <div className={style.recentlyPlayedIcon} />}
            </div>
            {isScheduledLiveGame && <div className={style.scheduledLiveGameBackground} />}
            {showFeatureIcons && (
                <div className={style.labels}>
                    {isGameNew && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.new')}</span>}
                    {isGamePopular && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.popular')}</span>}
                    {isGameHot && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.hot')}</span>}
                    {isGameExclusive && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.exclusive')}</span>}
                </div>
            )}
            {isScheduledLiveGame && (
                <div className={`${style.scheduledLiveGameInfo} ${showTitle ? style.withTitle : ''}`} aria-hidden={true}>
                    {openingDateTime
                        ? (
                            <>
                                <span>{t('playtech.mobile.system.liveGame.startsAt')}</span>
                                <span>{openingDateTime}</span>
                            </>
                        )
                        : <span>{t('playtech.mobile.system.liveGame.startsSoon')}</span>
                    }
                </div>
            )}
            <div className={`${style.sideIcons} ${showTitle ? style.withTitle : ''}`}>
                {isLiveGame && (
                    <div className={style.liveInfo} aria-hidden={true}>
                        <span>{dealerName}</span>
                        <span>{language}</span>
                    </div>
                )}
                <div className={style.spacer} />
                {!isPinned && showFavoriteIcon && !isLiveGame && (
                    <FavoriteIcon
                        code={code}
                        gameFeed={gameFeed}
                        className={style.favorite}
                    />
                )}
            </div>
            {isLiveGame && limits && <div className={style.liveLimits}>{limits}</div>}
            {isLiveGame && (
                <div className={style.seatsInfo} aria-hidden={true}>
                    <span>{seatsInfo}</span>
                    <span className={style.seatsIcon} />
                </div>
            )}
            {isPinned && <div className={style.titleWrapper}>
                <span className={`${style.name} ${style.ellipsis}`}>{name}</span>
                {showFavoriteIcon && (
                    <FavoriteIcon
                        code={code}
                        isFeatured={true}
                        gameFeed={gameFeed}
                        className={style.favorite}
                    />
                )}
            </div>}
        </a>
    );
};

export const isGameAvailable = compose(
    withProps(({ code }) => ({ isAvailable: Boolean(useGameConfigurationSelector(path(['games', code]))) })),
    branch(
        props => !props.isAvailable,
        renderNothing
    ),
    mapProps(omit(['isAvailable']))
);

export const enhance = compose(
    pure,
    withTranslations,
    isGameAvailable,
    branch(
        ({ showGameInfo }) => showGameInfo,
        withGameInfo,
        withGameLaunch
    ),
    withState('isLoadedGameImage', 'setLoadGameImage', false),
    withProps(() => usePortletConfig(prop('portletType'))),
    withHandlers(({
                      launchGame,
                      openGameInfo,
                      disableGameLaunch,
                      displayName,
                      mostPlayedGame,
                      showRecentlyPlayedIcon,
                      portletType,
                      categoryName
    }) => ({
        onClick: () => (event) => {
            const displayCategoryName = portletType === 'responsivegames' ? displayName : categoryName;
            const catName = displayCategoryName ? JSON.stringify({ displayCategoryName, mostPlayedGame }) : displayCategoryName;
            storageService.local.setItem('catName', catName);
            const onGameClick = disableGameLaunch ? identity : launchGame || openGameInfo;
            const isNotGamesPortlet = portletType !== 'responsivegames';
            event.preventDefault();
            const fn = () => onGameClick();
            showRecentlyPlayedIcon || isNotGamesPortlet ? onGameClick() : fn();
        }
    })),
    withProps(({ code, displayName, mostPlayedGame }) => ({
        displayName,
        mostPlayedGame,
        isDesktop: useDeviceSelector(prop('isDesktop')),
        ...useGameConfigurationSelector(path(['games', code])),
        ...useUserSelector(pick(['isLoggedIn']))
    })),
    shouldUpdate((props, nextProps) => {
        const isNotEqualLoad = props.isLoadedGameImage !== nextProps.isLoadedGameImage;

        return isNotEqualLoad;
    }),

    mapProps(omit(['description', 'demoModeSupport', 'jackpotCode']))
);

export const Game = enhance(GameBare);
